import type {
  CategoryItem,
  RefItem,
} from 'shared/experiments/components/NewSerp/types'

/**
 * Util uses the local cache: it is called frequently with the same arguments
 * over the large strings collection (400+ items)
 */
const getBreadcrumbs = (() => {
  const labelsCache: Record<string, readonly string[]> = {}

  return (label: string): readonly string[] => {
    if (labelsCache[label]?.length) {
      return labelsCache[label]
    }
    const breadcrumbs = label.split('/').map(l => l.trim())
    // Freeze the list, so no mutations (pop/shift etc.) can violate it
    labelsCache[label] = Object.freeze(breadcrumbs)

    return breadcrumbs
  }
})()

const getSimpleLabel = (label: string): string => {
  const breadcrumbs = getBreadcrumbs(label)

  return breadcrumbs[breadcrumbs.length - 1]
}

const getListWithoutCurrentCategory = (items: RefItem[]): CategoryItem[] => {
  return items
    .filter(({ label }) => label.match(/\//g)?.length === 1)
    .map(item => ({
      ...item,
      label: getSimpleLabel(item.label),
      level: 1,
    }))
}

const getCurrentCategoryItem = (currentCategory: RefItem): CategoryItem => {
  const breadcrumbs = getBreadcrumbs(currentCategory.label)

  return {
    ...currentCategory,
    label: breadcrumbs[breadcrumbs.length - 1],
    level: breadcrumbs.length - 1,
    hideCount: true,
  }
}

const getParentCategories = (currentCategory: RefItem): string[] => {
  const parentCategories = []
  const breadcrumbs = [...getBreadcrumbs(currentCategory.label)]

  // skip the 1st (current)
  while (breadcrumbs.pop()) {
    parentCategories.unshift(breadcrumbs.join('/'))
  }

  return parentCategories.filter(String)
}

const removeRootItemRef = (items: RefItem[]) => {
  if (items[0].label.indexOf('/') === -1) {
    items.shift()
  }
}

/**
 * Build list with parent categories (if present) for the item
 */
const buildParentCategories = (
  items: RefItem[],
  currentCategory: RefItem
): CategoryItem[] => {
  const parentCategories = getParentCategories(currentCategory)

  return items
    .map(item => {
      const { label } = item
      const breadcrumbs = getBreadcrumbs(label)

      if (parentCategories.includes(breadcrumbs.join('/'))) {
        return {
          ...item,
          label: breadcrumbs[breadcrumbs.length - 1],
          level: breadcrumbs.length - 1,
          hideCount: true,
        }
      }

      return null
    })
    .filter(Boolean) as CategoryItem[]
}

const buildCategoryItems = (
  items: RefItem[],
  currentRefItem: RefItem
): CategoryItem[] => {
  const currentCategoryItem = getCurrentCategoryItem(currentRefItem)
  const categories = [currentCategoryItem]

  items.forEach(item => {
    const { label } = item
    const breadcrumbs = getBreadcrumbs(label)

    if (
      breadcrumbs.includes(currentCategoryItem.label) &&
      breadcrumbs.length === currentCategoryItem.level + 2
    ) {
      categories.push({
        ...item,
        label: breadcrumbs[breadcrumbs.length - 1],
        level: currentCategoryItem.level + 1,
      })
    }
  })

  return categories
}

/**
 * Items collection will be mutated, but it is fine since deep cloning
 * would hit the performance during frequent calls of this util.
 */
export const parseCategories = (props: {
  items?: RefItem[]
  currentCategory?: string
}): CategoryItem[] => {
  const { items, currentCategory } = props || {}
  if (!items?.length) {
    return []
  }

  removeRootItemRef(items)
  if (!currentCategory) {
    return getListWithoutCurrentCategory(items)
  }

  const currentRefItem = items.find(item => item.label === currentCategory)
  if (!currentRefItem) {
    return []
  }

  const categories = buildParentCategories(items, currentRefItem)
  const entries = buildCategoryItems(items, currentRefItem)

  return categories.concat(entries)
}
